import { resourceApi } from '../../lib/email-resources-api';
import { emailKey } from '../../lib/email-api';
import { RecordEmailActions } from '../email/EmailWorkspace';
import { Emails } from '../email/Emails';
import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { RecordFieldValue, SchemaDescriptor, IndexPreferences } from '../../lib/api-types';
import { api } from '../../lib/api-client';
import { favoritesQuery, useSetFavorite, keys, recordQuery, schemaQuery, timelineQuery } from '../../lib/queries';
import { Icon } from '../ui/Icon';
import { Timeline } from './Timeline';
import { FieldValue } from './FieldValue';
import { RelatedRecords } from './RelatedRecords';
import { RecordEditor } from './RecordEditor';
import styles from './Crm.module.css';
import { isNoteObject, NotePage } from '../notes/Notes';

export function RecordDetail({ objectName, recordId }: { objectName: string; recordId: string }) {
  if (isNoteObject(objectName)) return <NotePage key={`${objectName}:${recordId}`} objectName={objectName} recordId={recordId} />;
  return <RecordDetailPage key={`${objectName}:${recordId}`} objectName={objectName} recordId={recordId} />;
}

type DetailTab = 'info' | 'timeline' | 'related' | 'view' | 'emails';

function RecordDetailPage({ objectName, recordId }: { objectName: string; recordId: string }) {
  const emailCapabilities=useQuery({queryKey:[...emailKey(),'capabilities'],queryFn:resourceApi.capabilities,retry:false});
  const favorites = useQuery(favoritesQuery());
  const setFavorite = useSetFavorite();
  const isFavorite = favorites.data?.favorites.some(item => item.recordId === recordId) ?? false;
  const [tab, setTab] = useState<DetailTab>('info');
  const [editing, setEditing] = useState(false);
  const [compact, setCompact] = useState(true);
  const [objectFilter, setObjectFilter] = useState('');
  const [search, setSearch] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);
  const tabId = useId();
  const schema = useQuery(schemaQuery());
  const queryClient = useQueryClient();
  const saveView = useMutation({
    mutationFn: (patch: IndexPreferences) => api.updateIndexPreferences(objectName, patch),
    onSuccess: result => {
      queryClient.setQueryData<SchemaDescriptor>(keys.schema, current => current && ({
        ...current, objects: current.objects.map(object => object.name === result.object || object.namePlural === objectName
          ? { ...object, indexPreferences: result.preferences } : object),
      }));
      setTab('info');
    },
  });
  const version = schema.data?.schemaVersion ?? 0;
  const detail = useQuery({
    ...recordQuery(version, objectName, recordId),
    enabled: Boolean(schema.data),
  });
  const timeline = useQuery({
    ...timelineQuery({ record: recordId, limit: 50 }),
    enabled: tab === 'timeline' && Boolean(detail.data),
  });

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    // Measure the space left by the app shell, including its sidebar.
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setCompact(entry.contentRect.width < 820);
    });
    observer.observe(body);
    return () => observer.disconnect();
  }, [Boolean(detail.data)]);

  const activeTab = !compact && tab === 'related' ? 'info' : tab;

  if (schema.isLoading || detail.isLoading) return <p className={styles.state}>Loading…</p>;
  if (schema.isError || detail.isError) return <p className={styles.state}>This record could not be loaded.</p>;
  if (!detail.data) return null;

  const { record, object } = detail.data;
  const schemaObject = schema.data?.objects.find(candidate => candidate.namePlural === objectName || candidate.name === objectName);
  const availableFields = record.fields.filter((field) => field.type !== 'position');
  const preferences = schema.data?.objects.find(candidate => candidate.namePlural === objectName || candidate.name === objectName)?.indexPreferences;
  const visibleFields = preferences?.detailFields === undefined ? availableFields
    : preferences.detailFields.flatMap(name => availableFields.filter(field => field.name === name));
  const tabs: Array<{ id: DetailTab; label: string }> = [
    { id: 'info', label: 'Info' },
    { id: 'timeline', label: 'Timeline' },
    ...((emailCapabilities.data?.enabled || record.fields.some(f => f.type === 'emails' && Array.isArray(f.value) && f.value.length > 0)) ? [{ id: 'emails' as const, label: 'Emails' }] : []),
    ...(compact ? [{ id: 'related' as const, label: 'Related' }] : []),
    { id: 'view', label: 'View' },
  ];
  const related = <RelatedRecords references={record.relations} objectFilter={objectFilter}
    onObjectFilter={setObjectFilter} search={search} onSearch={setSearch} />;

  return (
    <div className={`${styles.page} ${styles.recordDetailPage}`}>
      <header className={styles.toolbar}>
        <Link to="/app/o/$object" params={{ object: object.namePlural }} className={styles.action}>
          All {object.labelPlural}
        </Link>
        <button type="button" className={styles.action} aria-pressed={isFavorite}
          disabled={!favorites.data || favorites.isError || setFavorite.isPending}
          onClick={() => setFavorite.mutate({ recordId, favorite: !isFavorite })}>
          <Icon name="star" size={18} /> {isFavorite ? 'Remove favorite' : 'Add to favorites'}
        </button>
        <div className={styles.spacer} />
        <button type="button" className={`${styles.action} ${styles.actionPrimary}`} disabled={!schemaObject} onClick={() => setEditing(true)}>
          <Icon name="pencil" size={16} /> Edit
        </button>
      </header>
      {editing && schemaObject && <RecordEditor object={schemaObject} version={version} record={record}
        onClose={() => setEditing(false)} onSaved={() => { setEditing(false); setTab('info'); }} />}
      {setFavorite.isError && <p role="alert" className={styles.state}>Couldn’t update favorites. Try again.</p>}
      {favorites.isError && <p role="alert" className={styles.state}>Favorites could not be loaded. <button type="button" onClick={() => void favorites.refetch()}>Retry</button></p>}

      <div className={styles.body} ref={bodyRef}>
        <div className={styles.detailPage} data-contact-actions={preferences?.showContactOptions !== false}>
          <div className={styles.detailHeader}>
            <span className={styles.titleIcon}>
              <Icon name={object.iconKey} size={22} />
            </span>
            <h1 className={styles.detailTitle}>{record.displayName || 'Untitled'}</h1>
          </div>

          {preferences?.showContactOptions !== false && <div className={styles.contactDock}>
            <ContactAction label="Call" options={(detail.data.callOptions ?? []).map(option => ({
              id: `${option.sourceRecordId}:${option.fieldId}:${option.dialNumber}`,
              title: `${option.sourceRecordLabel} · ${option.label} · ${option.number}`, href: `tel:${option.dialNumber}`,
            }))} />
            <ContactAction label="Email" options={(detail.data.emailOptions ?? []).map(option => ({
              id: `${option.sourceRecordId}:${option.fieldId}:${option.address}`,
              title: `${option.sourceRecordLabel} · ${option.label} · ${option.address}`, href: `mailto:${encodeURIComponent(option.address)}`,
            }))} />
          </div>}
          <div className={styles.detail} data-compact={compact}>
            <section className={styles.panel}>
              <div className={styles.detailTabs} role="tablist" aria-label="Record sections">
                {tabs.map((item, index) => (
                  <button key={item.id} type="button" role="tab" id={`${tabId}-${item.id}`}
                    aria-controls={`${tabId}-${item.id}-panel`} aria-selected={activeTab === item.id}
                    tabIndex={activeTab === item.id ? 0 : -1} className={styles.detailTab}
                    disabled={saveView.isPending} onClick={() => setTab(item.id)}
                    onKeyDown={(event) => {
                      let next: number;
                      if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
                      else if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
                      else if (event.key === 'Home') next = 0;
                      else if (event.key === 'End') next = tabs.length - 1;
                      else return;
                      event.preventDefault();
                      const target = tabs[next]!;
                      setTab(target.id);
                      document.getElementById(`${tabId}-${target.id}`)?.focus();
                    }}>
                    {item.label}
                  </button>
                ))}
              </div>
              <div role="tabpanel" id={`${tabId}-info-panel`} aria-labelledby={`${tabId}-info`}
                hidden={activeTab !== 'info'} tabIndex={0}>
                {visibleFields.map((field) => <FieldRow key={field.id} field={field} />)}
              </div>
              <div role="tabpanel" id={`${tabId}-timeline-panel`} aria-labelledby={`${tabId}-timeline`}
                hidden={activeTab !== 'timeline'} tabIndex={0}>
                {activeTab === 'timeline' && (timeline.isLoading ? (
                  <p className={styles.state}>Loading…</p>
                ) : timeline.isError ? (
                  <div className={styles.state} role="alert">
                    <p>The timeline could not be loaded.</p>
                    <button type="button" className={styles.action} onClick={() => void timeline.refetch()}>Try again</button>
                  </div>
                ) : <Timeline events={timeline.data?.events ?? []} />)}
              </div>
              {activeTab === 'emails' && <div role="tabpanel" id={`${tabId}-emails-panel`} aria-labelledby={`${tabId}-emails`}><RecordEmailActions recordId={recordId}/><Emails recordId={recordId}/></div>}
              {activeTab === 'view' && <div role="tabpanel" id={`${tabId}-view-panel`} aria-labelledby={`${tabId}-view`}>
                <DetailViewEditor fields={availableFields} selected={visibleFields.map(field => field.name)}
                  label={object.labelPlural} showContacts={preferences?.showContactOptions !== false} saving={saveView.isPending} error={saveView.isError}
                  onSave={patch => saveView.mutate(patch)} onCancel={() => { saveView.reset(); setTab('info'); }} />
              </div>}
              {compact && <div role="tabpanel" id={`${tabId}-related-panel`} aria-labelledby={`${tabId}-related`}
                hidden={activeTab !== 'related'} tabIndex={0}>{related}</div>}
            </section>

            {!compact && <aside className={styles.panel} aria-label="Related records">
              <h2 className={styles.relatedTitle}>Related</h2>
              {related}
            </aside>}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ field }: { field: RecordFieldValue }) {
  return (
    <div className={styles.fieldRow}>
      <span className={styles.fieldLabel}>{field.label}</span>
      <div className={styles.fieldValue}>
        <FieldValue field={field} />
      </div>
    </div>
  );
}

function DetailViewEditor({ fields, selected, label, showContacts, saving, error, onSave, onCancel }: {
  fields: RecordFieldValue[]; selected: string[]; label: string; showContacts: boolean; saving: boolean; error: boolean;
  onSave: (patch: IndexPreferences) => void; onCancel: () => void;
}) {
  const [draft, setDraft] = useState(selected);
  const [contacts, setContacts] = useState(showContacts);
  const move = (index: number, delta: number) => setDraft(current => {
    const next = [...current];
    [next[index], next[index + delta]] = [next[index + delta]!, next[index]!];
    return next;
  });
  return <section className={styles.columnEditor} aria-label="Detail view fields">
    <p>Choose visible fields and their order. Save applies to all {label} for your account on web and iOS.</p>
    <label><input type="checkbox" checked={contacts} disabled={saving} onChange={event => setContacts(event.target.checked)} /> Show contact options</label>
    <ol>{draft.map((name, index) => <li key={name}>
      <span>{fields.find(field => field.name === name)?.label ?? name}</span>
      <button type="button" disabled={saving || index === 0} onClick={() => move(index, -1)} aria-label={`Move ${name} up`}>↑</button>
      <button type="button" disabled={saving || index === draft.length - 1} onClick={() => move(index, 1)} aria-label={`Move ${name} down`}>↓</button>
      <button type="button" disabled={saving} onClick={() => setDraft(current => current.filter(field => field !== name))} aria-label={`Hide ${name}`}>Hide</button>
    </li>)}</ol>
    <select aria-label="Add field" value="" disabled={saving} onChange={event => setDraft([...draft, event.target.value])}>
      <option value="" disabled>Add field…</option>
      {fields.filter(field => !draft.includes(field.name)).map(field => <option key={field.id} value={field.name}>{field.label}</option>)}
    </select>
    {error && <p role="alert">The view could not be saved. Try again.</p>}
    <div className={styles.columnActions}>
      <button type="button" className={styles.action} disabled={saving} onClick={() => onSave({ detailFields: draft, showContactOptions: contacts })}>{saving ? 'Saving…' : 'Save'}</button>
      <button type="button" className={styles.action} disabled={saving} onClick={onCancel}>Cancel</button>
    </div>
  </section>;
}

function ContactAction({ label, options }: { label: string; options: Array<{ id: string; title: string; href: string }> }) {
  const [open, setOpen] = useState(false);
  const icon = <Icon name={label === 'Call' ? 'phone' : 'mail'} size={24} />;
  const className = `${styles.contactCircle} ${label === 'Call' ? styles.callCircle : styles.emailCircle}`;
  if (!options.length) return <button type="button" className={className} disabled aria-label={label} title={`No ${label === 'Call' ? 'phone numbers' : 'email addresses'} available`}>{icon}</button>;
  if (options.length === 1) return <a className={className} aria-label={label} href={options[0]!.href}>{icon}</a>;
  return <div className={styles.contactPicker}>
    <button type="button" aria-label={label} className={className} aria-expanded={open} onClick={() => setOpen(!open)}>{icon}</button>
    {open && <ul className={styles.contactMenu} aria-label={`Choose ${label.toLowerCase()} recipient`}>
      {options.map(option => <li key={option.id}><a href={option.href} onClick={() => setOpen(false)}>{option.title}</a></li>)}
    </ul>}
  </div>;
}
